import React from 'react';
import { Helmet } from 'react-helmet-async';

const GA_ID = 'G-LS0F4WKJMR';

/**
 * GA4 + global event delegation (Phase 6).
 *
 * The inline script wires every conversion event named in the prompt without
 * requiring per-link instrumentation. Components can opt into richer payloads
 * with data-analytics-* attributes (see StickyCallButton for an example) or
 * call the typed `track()` helper from src/lib/analytics/events.
 *
 * Tracked events:
 *   click_to_call, click_to_text, form_submit_quote, click_review_card,
 *   scroll_75, scroll_complete, outbound_gbp_click, outbound_facebook_click,
 *   hub_to_spoke_click, location_page_cta_click.
 */
const Analytics: React.FC = () => {
  return (
    <Helmet>
      <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}></script>
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', '${GA_ID}', { send_page_view: true });

        function pushEvent(name, params){
          var p = Object.assign({ page: location.pathname }, params || {});
          window.dataLayer.push(Object.assign({ event: name }, p));
          if (typeof gtag === 'function') gtag('event', name, p);
        }

        // --- Click delegation -------------------------------------------------
        document.addEventListener('click', function(e){
          var el = e.target.closest('a, button');
          if (!el) return;
          // Digital cards emit their own source-aware events through track().
          if (el.closest('[data-digital-card]')) return;
          var href = el.getAttribute('href') || '';
          var dataset = el.dataset || {};
          var label = (el.innerText || '').trim().slice(0, 80);
          var locTag = dataset.analyticsLocation || '';
          var host = window.location.host;

          // Explicit data-analytics-event override (highest priority).
          if (dataset.analyticsEvent) {
            pushEvent(dataset.analyticsEvent, { label: label, destination: href, location: locTag });
            return;
          }

          if (href.indexOf('tel:') === 0) {
            pushEvent('click_to_call', { destination: href, label: label, location: locTag });
            return;
          }
          if (href.indexOf('sms:') === 0) {
            pushEvent('click_to_text', { destination: href, label: label, location: locTag });
            return;
          }
          if (href.indexOf('mailto:') === 0) {
            pushEvent('email_click', { destination: href, label: label });
            return;
          }
          // Outbound link bucket.
          if (/^https?:\\/\\//i.test(href) && href.indexOf(host) === -1) {
            if (/google\\.com|maps\\.google/i.test(href)) {
              pushEvent('outbound_gbp_click', { destination: href, label: label });
            } else if (/facebook\\.com/i.test(href)) {
              pushEvent('outbound_facebook_click', { destination: href, label: label });
            }
          }
          // Internal hub -> spoke navigation (3+ path segments under /services/).
          if (/^\\/services\\/[^/]+\\/[^/]+/i.test(href)) {
            pushEvent('hub_to_spoke_click', { destination: href, label: label });
          }
          // Location page CTA bucket.
          if (locTag && locTag.indexOf('location_page') === 0 && /^(\\/(contact)|tel:)/i.test(href)) {
            pushEvent('location_page_cta_click', { destination: href, label: label, location: locTag });
          }
        }, { passive: true });

        // --- Form submission --------------------------------------------------
        document.addEventListener('submit', function(e){
          var form = e.target;
          if (!form || form.tagName !== 'FORM') return;
          var name = form.getAttribute('name') || form.id || '';
          if (/quote|contact|estimate/i.test(name) || form.matches('[data-analytics-form="quote"]')) {
            pushEvent('form_submit_quote', { label: name });
          }
        }, true);

        // --- Scroll depth (75 / 100) -----------------------------------------
        // rAF-throttled: we read layout values (scrollTop, scrollHeight) at
        // most once per frame, AFTER the browser's own layout pass, so this
        // handler never triggers a synchronous forced reflow.
        var scroll75 = false, scroll100 = false, scrollQueued = false;
        function checkScroll(){
          scrollQueued = false;
          if (scroll75 && scroll100) return;
          var doc = document.documentElement;
          var scrolled = (doc.scrollTop + window.innerHeight);
          var pct = scrolled / doc.scrollHeight;
          if (!scroll75 && pct >= 0.75) {
            scroll75 = true;
            pushEvent('scroll_75');
          }
          if (!scroll100 && pct >= 0.97) {
            scroll100 = true;
            pushEvent('scroll_complete');
          }
        }
        window.addEventListener('scroll', function(){
          if (scrollQueued) return;
          scrollQueued = true;
          requestAnimationFrame(checkScroll);
        }, { passive: true });
      `}</script>
      <meta name="google-site-verification" content="GOOGLE_SEARCH_CONSOLE_VERIFICATION_CODE" />
    </Helmet>
  );
};

export default Analytics;
